import { effect, inject, Injectable, untracked } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { APP_ROUTES } from '@core/routes';
import { agentAccount } from '@features/auth/store/auth.signals';
import { CustomerApiService } from '@features/customer/services/customer-api.service';
import { activeCustomer } from '@features/customer/store/work-with-customer.signals';
import { OrganizeFoldersChatService } from '@features/folders/chat/organize-folders-chat.service';
import { SavedSearchChatService } from '@features/saved-search/chat/saved-search-chat.service';
import { filter } from 'rxjs';
import { InvitationStatus } from 'src/.graphql/types';
import { CHATBOT_CONTEXT_IDS } from '../constants/chatbot-contexts';
import { CHATBOT_GREETING_WORKFLOWS, GreetingWorkflowButtonDef } from '../constants/chatbot-greeting-workflows.const';
import { ChatbotContextService } from './chatbot-context';
import { ChatbotEventNotificationService } from './chatbot-event-notification.service';
import { ChatbotGreetingService, GreetingButton } from './chatbot-greeting.service';
import { ChatbotSessionService } from './chatbot-session.service';

type ActiveCustomer = { id: number; firstName: string; lastName: string; email: string; invitationStatus?: InvitationStatus } | null;

@Injectable({ providedIn: 'root' })
export class ChatbotPageWorkflowService {
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly greetingService = inject(ChatbotGreetingService);
    private readonly sessionService = inject(ChatbotSessionService);
    private readonly savedSearchChatService = inject(SavedSearchChatService);
    private readonly organizeFoldersChatService = inject(OrganizeFoldersChatService);
    private readonly contextService = inject(ChatbotContextService);
    private readonly customerApiService = inject(CustomerApiService);
    private readonly eventNotificationService = inject(ChatbotEventNotificationService);

    constructor() {
        effect(() => {
            activeCustomer();
            untracked(() => this.updateGreeting());
        });
    }

    initialize(): void {
        this.router.events
            .pipe(filter(event => event instanceof NavigationEnd))
            .subscribe(() => this.updateGreeting());

        this.updateGreeting();
    }

    private updateGreeting(): void {
        const pageKey = this.getPageKey();
        const customer = activeCustomer();
        const buttons = this.buildButtons(pageKey, customer);

        if (!buttons.length) {
            this.greetingService.clear();

            return;
        }

        this.greetingService.set({
            text: this.getTimeGreeting(this.getFirstName()),
            subtext: 'How can I help you today? Here are some suggested options to get you started, or you can describe what you\'d like to do.',
            buttons,
        });
    }

    private buildButtons(pageKey: string, customer: ActiveCustomer): GreetingButton[] {
        switch (pageKey) {
            case 'search':
                return this.buildSearchButtons(customer);
            case 'listing-details':
                return this.buildListingDetailsButtons(customer);
            case 'customers':
                return this.buildCustomersButtons(customer);
            default: {
                const defs = CHATBOT_GREETING_WORKFLOWS[pageKey];

                return defs ? defs.map(def => this.defToButton(def)) : [];
            }
        }
    }

    private buildSearchButtons(customer: ActiveCustomer): GreetingButton[] {
        const customerButton: GreetingButton = customer
            ? {
                label: `Run my saved search for ${customer.firstName}`,
                subtitle: 'Run a saved search for your active customer',
                onClick: () => this.sessionService.submitMessage(`Run my saved search for ${customer.firstName} ${customer.lastName}`),
            }
            : {
                label: 'Find listings for a customer',
                subtitle: 'Search listings for one of your customers',
                onClick: () => this.sessionService.submitMessage('Find listings for a customer'),
            };

        return [
            customerButton,
            {
                label: 'Show me what\'s new on the market',
                subtitle: 'Get the latest new inventory',
                onClick: () => this.sessionService.submitMessage('Show me what\'s new on the market'),
            },
            {
                label: 'Generate a comp report',
                subtitle: 'Build a comparative market analysis',
                onClick: () => this.sessionService.submitMessage('Generate a comp report'),
            },
            {
                label: 'Save this search',
                subtitle: 'Save current filters for future use',
                onClick: () => this.savedSearchChatService.openSaveSearchInChat(),
            },
        ];
    }

    private buildListingDetailsButtons(customer: ActiveCustomer): GreetingButton[] {
        const buttons: GreetingButton[] = [
            {
                label: 'Give me insights about this listing',
                subtitle: 'AI-generated insights for this listing',
                onClick: () => {
                    const id = this.getCurrentListingId();

                    this.sessionService.submitMessage(id ? `Give me insights about listing ID ${id}` : 'Give me insights about this listing');
                },
            },
            {
                label: 'Show me a buyer profile for this listing',
                subtitle: 'See the ideal buyer for this listing',
                onClick: () => {
                    const id = this.getCurrentListingId();

                    this.sessionService.submitMessage(id ? `Show me a buyer profile for listing ID ${id}` : 'Show me a buyer profile for this listing');
                },
            },
            {
                label: 'Create a comp report based on this listing',
                subtitle: 'Build a CMA using this listing as the subject',
                onClick: () => {
                    const id = this.getCurrentListingId();

                    this.sessionService.submitMessage(id ? `Create a comp report using listing ID ${id} as the subject property` : 'Create a comp report using this listing as the subject property');
                },
            },
        ];

        if (customer) {
            buttons.push({
                label: `Add this listing to ${customer.firstName}'s portfolio`,
                subtitle: 'Organize this listing for your active customer',
                onClick: () => {
                    const id = this.getCurrentListingId();

                    if (id) {
                        this.organizeFoldersChatService.openOrganizeFoldersInChat([Number(id)]);
                    }
                },
            });
        }

        return buttons;
    }

    private getCurrentListingId(): string | null {
        const item = this.contextService.context().find(c => c.id === CHATBOT_CONTEXT_IDS.LISTING_DETAILS);

        return item?.data?.['listingId'] ?? null;
    }

    private getCurrentBuildingId(): string | null {
        const item = this.contextService.context().find(c => c.id === CHATBOT_CONTEXT_IDS.BUILDING_DETAILS);

        return item?.data?.['buildingId'] ?? null;
    }

    private getCurrentNeighborhoodId(): string | null {
        const item = this.contextService.context().find(c => c.id === CHATBOT_CONTEXT_IDS.NEIGHBORHOOD_DETAILS);

        return item?.data?.['neighborhoodId'] ?? null;
    }

    private buildCustomersButtons(customer: ActiveCustomer): GreetingButton[] {
        if (customer) {
            return [
                {
                    label: `Show me new matches for ${customer.firstName}`,
                    subtitle: 'View new listing matches for this customer',
                    onClick: () => this.sessionService.submitMessage(`Show me new matches for ${customer.firstName} ${customer.lastName}`),
                },
                this.buildMyPlaceButton(customer),
                {
                    label: `Create a customer snapshot report for ${customer.firstName}`,
                    subtitle: 'Generate a snapshot report for this customer',
                    onClick: () => this.sessionService.submitMessage(`Create a customer snapshot report for ${customer.firstName} ${customer.lastName}`),
                },
            ];
        }

        return [
            {
                label: 'Show me my customers with new matches',
                subtitle: 'Filter customers by new listing activity',
                onClick: () => this.sessionService.submitMessage('Show me my customers with new matches'),
            },
            {
                label: 'Show me customers with recent myPlace activity',
                subtitle: 'Filter customers by myPlace engagement',
                onClick: () => this.sessionService.submitMessage('Show me my customers with recent myPlace activity'),
            },
            {
                label: 'Create a customer snapshot report',
                subtitle: 'Generate a snapshot report for a customer',
                onClick: () => this.sessionService.submitMessage('Create a customer snapshot report'),
            },
        ];
    }

    private buildMyPlaceButton(customer: NonNullable<ActiveCustomer>): GreetingButton {
        if (customer.invitationStatus === 'ACTIVE') {
            return {
                label: `Show me recent myPlace activity for ${customer.firstName}`,
                subtitle: 'See recent myPlace activity for this customer',
                onClick: () => this.sessionService.submitMessage(`Show me recent myPlace activity for ${customer.firstName} ${customer.lastName}`),
            };
        }

        if (customer.invitationStatus === 'INVITED') {
            return {
                label: `Invitation already sent to ${customer.firstName}`,
                subtitle: 'Waiting for them to accept their myPlace invitation',
                onClick: () => this.sessionService.submitMessage(`What's the status of ${customer.firstName} ${customer.lastName}'s myPlace invitation?`),
            };
        }

        return {
            label: `Invite ${customer.firstName} to collaborate in myPlace`,
            subtitle: 'Send a myPlace collaboration invitation',
            onClick: () => void this.inviteCustomerToMyPlace(customer),
        };
    }

    private async inviteCustomerToMyPlace(customer: NonNullable<ActiveCustomer>): Promise<void> {
        const success = await this.customerApiService.inviteCustomer(customer.id, customer.email).catch(() => false);

        this.eventNotificationService.notify({
            message: success
                ? `Invitation sent to ${customer.firstName} ${customer.lastName}.`
                : `Couldn't send the invitation to ${customer.firstName}. Please try again.`,
        });
    }

    private defToButton(def: GreetingWorkflowButtonDef): GreetingButton {
        return {
            label: def.label,
            subtitle: def.subtitle,
            iconName: def.iconName,
            onClick: this.resolveAction(def.actionKey),
            wip: def.wip,
        };
    }

    private resolveAction(actionKey: string): () => void {
        switch (actionKey) {
            case 'showNewMarketInventory':
                return () => this.sessionService.submitMessage('Show me what\'s new on the market');
            case 'generateCompReport':
                return () => this.sessionService.submitMessage('Generate a comp report');
            case 'showCustomersWithActivity':
                return () => this.sessionService.submitMessage('Show me which customers have new matches or other activity I need to review');
            case 'saveSearch':
                return () => this.savedSearchChatService.openSaveSearchInChat();
            case 'compareTopPicks':
                return () => void this.router.navigate([APP_ROUTES.CMA_SEARCH]);
            case 'buildingCompReport':
                return () => {
                    const id = this.getCurrentBuildingId();

                    this.sessionService.submitMessage(id
                        ? `Run a comparative market analysis (CMA) for building ID ${id}. Use this building as the subject.`
                        : 'Run a comparative market analysis (CMA) for this building. Use the building from my current page context as the subject.');
                };
            case 'buildingBuyerProfile':
                return () => {
                    const id = this.getCurrentBuildingId();

                    this.sessionService.submitMessage(id ? `Show me a buyer profile for building ID ${id}` : 'Show me a buyer profile for this building');
                };
            case 'buildingInsights':
                return () => {
                    const id = this.getCurrentBuildingId();

                    this.sessionService.submitMessage(id ? `Give me insights about building ID ${id}` : 'Give me insights for this building');
                };
            case 'buildingMarketInventory':
                return () => {
                    const id = this.getCurrentBuildingId();

                    this.sessionService.submitMessage(id ? `Tell me what's currently on the market in building ID ${id}` : 'Tell me what\'s currently on the market in this building');
                };
            case 'buildingBrochure':
                return () => {
                    const id = this.getCurrentBuildingId();

                    this.sessionService.submitMessage(id ? `Create a brochure for building ID ${id} and its neighborhood` : 'Create a building and neighborhood brochure');
                };
            case 'neighborhoodInsights':
                return () => {
                    const id = this.getCurrentNeighborhoodId();

                    this.sessionService.submitMessage(id ? `Give me insights about neighborhood ID ${id}` : 'Give me insights for this neighborhood');
                };
            case 'neighborhoodBuyerProfile':
                return () => {
                    const id = this.getCurrentNeighborhoodId();

                    this.sessionService.submitMessage(id ? `Show me a buyer profile for neighborhood ID ${id}` : 'Show me a buyer profile for this neighborhood');
                };
            case 'neighborhoodMarketInventory':
                return () => {
                    const id = this.getCurrentNeighborhoodId();

                    this.sessionService.submitMessage(id ? `Tell me what's currently on the market in neighborhood ID ${id}` : 'Tell me what\'s currently on the market in this neighborhood');
                };
            case 'neighborhoodBrochure':
                return () => {
                    const id = this.getCurrentNeighborhoodId();

                    this.sessionService.submitMessage(id ? `Create a brochure for neighborhood ID ${id}` : 'Create a neighborhood brochure');
                };
            default:
                return () => { };
        }
    }

    private getTimeGreeting(firstName: string): string {
        const hour = new Date().getHours();

        if (hour < 12) {
            return `Good morning, ${firstName}!`;
        }

        if (hour < 17) {
            return `Good afternoon, ${firstName}!`;
        }

        return `Good evening, ${firstName}!`;
    }

    private getFirstName(): string {
        const fullName = agentAccount()?.fullName ?? '';
        const spaceIndex = fullName.indexOf(' ');

        return spaceIndex === -1 ? fullName : fullName.substring(0, spaceIndex);
    }

    private getPageKey(): string {
        let route = this.activatedRoute;

        while (route.firstChild) {
            route = route.firstChild;
        }

        return ((route.snapshot.data['key'] as string | null) ?? '').replace('prnyc-', '');
    }
}
